import { useEffect, useState, type ReactElement } from 'react';
import { Share, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/lib/i18n';
import {
  dismissedInstall,
  installAdvice,
  installedAlready,
  isIosBrowser,
  rememberDismissed,
  type InstallAdvice,
} from '@/lib/install-prompt';

// Chrome's own event, which must be kept to be answered later. Older Chromes
// answer through `userChoice`; newer ones resolve `prompt()` with the outcome.
type Chosen = { outcome: 'accepted' | 'dismissed' };
type InstallEvent = Event & {
  prompt: () => Promise<Chosen | void>;
  userChoice?: Promise<Chosen>;
};

// iOS has no install API: Safari installs from its Share menu and offers a page
// nothing, so the best this can be there is that menu spelled out. §20
function Steps({ onClose }: { onClose: () => void }): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-70 flex items-center justify-center p-4 pt-safe pb-safe">
      <button
        type="button"
        aria-label={t('close')}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/40"
      />
      <div
        role="dialog"
        aria-label={t('installTitle')}
        className="relative flex w-full max-w-[380px] flex-col gap-3 rounded-2xl border bg-card p-4 shadow-2xl"
      >
        <h3 className="text-[15px] font-bold">{t('installTitle')}</h3>
        <ol className="flex list-none flex-col gap-2.5 text-[13.5px] leading-snug">
          <li className="flex items-start gap-2">
            <Share className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
            <span>{t('installStep1')}</span>
          </li>
          <li className="ms-6">{t('installStep2')}</li>
          <li className="ms-6">{t('installStep3')}</li>
        </ol>
        <p className="text-[11.5px] text-muted-foreground">{t('installSafariOnly')}</p>
        <Button className="w-full" onClick={onClose}>
          {t('close')}
        </Button>
      </div>
    </div>
  );
}

// One row under the header, dismissable for good, and never in the way: §20
// gives the controls a third of a phone at most, and this is not a control.
export function InstallBanner(): ReactElement | null {
  const { t } = useTranslation();
  const [offer, setOffer] = useState<InstallEvent | null>(null);
  const [advice, setAdvice] = useState<InstallAdvice>('hidden');
  const [steps, setSteps] = useState(false);
  const [gone, setGone] = useState(() => dismissedInstall(window.localStorage));

  useEffect(() => {
    const decide = (event: InstallEvent | null): void => {
      if (event !== null) setOffer(event);
      setAdvice(
        installAdvice({
          standalone: installedAlready(window),
          ios: isIosBrowser(window.navigator),
          canPrompt: event !== null,
        }),
      );
    };
    const caught = (event: Event): void => {
      // Kept, not fired: the browser's own bar is replaced by this one.
      event.preventDefault();
      decide(event as InstallEvent);
    };
    window.addEventListener('beforeinstallprompt', caught);
    decide(null);
    return () => window.removeEventListener('beforeinstallprompt', caught);
  }, []);

  if (gone || advice === 'hidden') return null;

  const dismiss = (): void => {
    rememberDismissed(window.localStorage);
    setGone(true);
  };

  return (
    <>
      <div
        role="note"
        aria-label={t('installTitle')}
        className="flex shrink-0 items-center gap-2 border-b bg-accent/10 px-3 py-2 text-[12.5px]"
      >
        <Share className="size-4 shrink-0 text-primary" aria-hidden />
        <p className="min-w-0 flex-1">
          <b className="font-semibold">{t('installTitle')}</b>{' '}
          {/* Why, in one line; the how is behind Show me, so this stays short. */}
          <span className="text-muted-foreground">{t('installLead')}</span>
        </p>
        <Button
          size="sm"
          className="h-7 shrink-0 px-2 text-[12px]"
          onClick={advice === 'prompt' ? () => void install() : () => setSteps(true)}
        >
          {t(advice === 'prompt' ? 'installAction' : 'installShow')}
        </Button>
        <button
          type="button"
          aria-label={t('close')}
          onClick={dismiss}
          className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-secondary"
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>
      {steps && <Steps onClose={() => setSteps(false)} />}
    </>
  );

  // Declining the browser's own dialog is not dismissing the row for good: it
  // goes for this visit, and the next launch may find them readier.
  async function install(): Promise<void> {
    const answered = await offer?.prompt();
    const outcome = (answered ?? (await offer?.userChoice))?.outcome;
    if (outcome === 'dismissed') setGone(true);
    else dismiss();
  }
}
